import { HttpError } from '@brunommsilva/react-foundations';

import { authApi } from '../../apis/accounting-api/auth/auth-api';
import tokenStorage from './token-storage';

export type DecodedToken = {
  exp: number;
  sub: string;
  [key: string]: unknown;
};

class AuthService {
  private static instance: AuthService;
  private refreshPromise: Promise<string> | null = null;

  private constructor() {}

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private decodeToken(token: string): DecodedToken | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  public isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) {
      return true;
    }
    // 10 second buffer
    return Date.now() >= (decoded.exp - 10) * 1000;
  }

  public isLogged(): boolean {
    const accessToken = tokenStorage.getAccessToken();
    const refreshToken = tokenStorage.getRefreshToken();

    if (!accessToken) {
      console.log('[AuthService] No access token found');
      return false;
    }

    // Still logged in if access is valid OR refresh is valid
    if (!this.isTokenExpired(accessToken)) {
      console.log('[AuthService] Access token is valid');
      return true;
    }

    if (refreshToken && !this.isTokenExpired(refreshToken)) {
      console.log('[AuthService] Refresh token is valid');
      return true;
    }

    console.log('[AuthService] Both tokens are expired or invalid');
    return false;
  }

  public onLogin(accessToken: string, refreshToken?: string): void {
    tokenStorage.setAccessToken(accessToken);
    if (refreshToken) {
      tokenStorage.setRefreshToken(refreshToken);
    }
  }

  public onLogout(): void {
    tokenStorage.clearTokens();
  }

  public async refreshToken(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const currentRefreshToken = tokenStorage.getRefreshToken();
    if (!currentRefreshToken || this.isTokenExpired(currentRefreshToken)) {
      this.onLogout();
      throw new HttpError(401, 'Refresh token expired or missing');
    }

    this.refreshPromise = (async () => {
      try {
        const tokens = await authApi.refresh(currentRefreshToken);
        this.onLogin(tokens.accessToken, tokens.refreshToken);
        return tokens.accessToken;
      } catch (error) {
        if (HttpError.isAuthFailure(error) || HttpError.isBadRequest(error)) {
          this.onLogout();
        }
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }
}

const authService = AuthService.getInstance();
export default authService;
